/**
 * 코스 진행 — LoadResult.bodies(시작·결승 zone, env 표면)와 프레임 자세만으로 계산한다(커널 계약 밖 정보 없음).
 *  - 진행률 = 시작 zone 중심 → 결승 zone 중심 축에 내 차체를 투영한 비율(0..1)
 *  - 남은 거리(SU HUD '남은 거리 18.4m') = (1 - 진행률) × 축 길이
 *  - 발밑 표면(SU 결과 '멈춘 곳 언덕 62%') = 내 위치를 덮는 env 몸체의 surface
 */
import type { BodyDesc, Frame } from "@picnic-field/local-host/protocol";
import type { CourseAxis } from "../types";

const pos = (tf: Float32Array, i: number): [number, number, number] => [
  tf[i * 7] ?? 0,
  tf[i * 7 + 1] ?? 0,
  tf[i * 7 + 2] ?? 0,
];

/**
 * 시작 → 결승 축. 시작은 surface "start" 인 몸체 — 커널 코스의 출발판(start-pad)은 **kind "env"**(바닥 판)이고 결승만 zone 이다
 * (2026-10-09 — zone 만 찾아서 실제 코스에서는 축이 없었다: SE 칸 0 · SU 남은 거리 0.0m 고정. 가짜 호스트 · 시험만 시작을 zone 으로 두었다).
 * 출발판이 없는 코스(라인 · 산책)는 `fallbackStart`(첫 프레임의 내 기계 자리)를 시작으로 쓴다.
 */
export function courseAxis(
  bodies: readonly BodyDesc[],
  tf: Float32Array,
  fallbackStart?: readonly number[],
): CourseAxis | null {
  const s = bodies.find((b) => b.owner === "env" && b.surface === "start");
  const f = bodies.find((b) => b.kind === "zone" && b.surface === "finish");
  if (!f || (!s && !fallbackStart)) return null;
  const start: [number, number, number] = s
    ? pos(tf, s.index)
    : [fallbackStart![0] ?? 0, fallbackStart![1] ?? 0, fallbackStart![2] ?? 0];
  const finish = pos(tf, f.index);
  const length = Math.hypot(finish[0] - start[0], finish[2] - start[2]);
  return length > 1e-6 ? { start, finish, length } : null;
}

/** 수평면 투영 진행률 0..1 */
export function progressOf(axis: CourseAxis, p: readonly number[]): number {
  const ax = axis.finish[0] - axis.start[0];
  const az = axis.finish[2] - axis.start[2];
  const t =
    (((p[0] ?? 0) - axis.start[0]) * ax + ((p[2] ?? 0) - axis.start[2]) * az) / axis.length ** 2;
  return Math.max(0, Math.min(1, t));
}

export function remainingMeters(axis: CourseAxis, progress: number): number {
  return Math.max(0, (1 - progress) * axis.length);
}

/** SE 진행 막대 칸(🏁 + 채운 칸 · 빈 칸 — 화면은 CSS 상자 .pf-hud__cell) — 5칸 중 채운 칸 수 */
export function progressCells(progress: number, cells = 5): number {
  return Math.max(0, Math.min(cells, Math.floor(progress * cells + 1e-9)));
}

/** 내 위치를 덮는 env 표면(몸체 자세의 수평 발자국 — 회전은 yaw 만 고려하지 않는 근사: 코스 env 는 축 정렬) */
export function surfaceAt(
  bodies: readonly BodyDesc[],
  tf: Float32Array,
  p: readonly number[],
): string | null {
  let best: { surface: string; top: number } | null = null;
  for (const b of bodies) {
    if (b.kind !== "env" || !b.surface || !b.shape) continue;
    const [cx, cy, cz] = pos(tf, b.index);
    let hx: number;
    let hz: number;
    let hy: number;
    if (b.shape.type === "cylinder") {
      hx = hz = b.shape.radius;
      hy = b.shape.halfHeight;
    } else {
      [hx, hy, hz] = b.shape.half; // ramp 도 half 상자 안의 쐐기 — 발자국은 같다
    }
    if (Math.abs((p[0] ?? 0) - cx) > hx || Math.abs((p[2] ?? 0) - cz) > hz) continue;
    const top = cy + hy;
    // 겹치면 윗면이 높은 것(언덕 · 턱이 바닥보다 앞선다)
    if (!best || top > best.top) best = { surface: b.surface, top };
  }
  return best?.surface ?? null;
}

/** 프레임에서 따라갈 몸체 위치 */
export function followPos(frame: Frame, followIndex: number): [number, number, number] {
  return pos(frame.transforms, followIndex);
}

/** 시간 표기 — SE 'm:ss' · SU 'm:ss.d' (내림) */
export function formatClock(seconds: number, precise: boolean): string {
  const s = Math.max(0, seconds);
  if (precise) {
    const tenths = Math.floor(s * 10 + 1e-6);
    const m = Math.floor(tenths / 600);
    const rest = tenths - m * 600;
    return `${m}:${String(Math.floor(rest / 10)).padStart(2, "0")}.${rest % 10}`;
  }
  const whole = Math.floor(s + 1e-6);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}
